import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys, saveModelSettings, useModelSettings } from "@/api/queries";
import { Button } from "@/components/Button";
import { Panel } from "@/components/Panel";
import type { ModelSettings } from "@/api/types";
import { runtimeSummary } from "@/lib/model-runtime";

/**
 * 安装级设置；密码仅留在受控输入与本次请求中。
 *
 * @dataSource remote
 */
export function ModelSettingsView() {
  const query = useModelSettings();
  const client = useQueryClient();
  const [notice, setNotice] = useState("");
  return (
    <Panel title="模型配置" description="OneLinkAI · 配置存数据库，API Key 存本机系统凭据库">
      {notice && (
        <p role="status" className="inline-note">
          {notice}
        </p>
      )}
      {query.isPending ? (
        <p>加载配置中…</p>
      ) : query.isError ? (
        <div role="alert">
          <p>配置加载失败。仅支持本机访问，请检查后端与权限。</p>
          <Button onClick={() => void query.refetch()}>重新加载</Button>
        </div>
      ) : query.data ? (
        <SettingsForm
          key={query.data.revision}
          current={query.data}
          onSaved={(data) => {
            setNotice(
              data.cleanup_pending
                ? "配置已保存，但旧凭据清理失败，请在系统凭据管理器中清理。"
                : "配置已保存；未进行在线连接验证。",
            );
            client.setQueryData(queryKeys.modelSettings, data);
          }}
          onReload={() => void query.refetch()}
        />
      ) : null}
    </Panel>
  );
}

function SettingsForm({
  current,
  onSaved,
  onReload,
}: {
  current: ModelSettings;
  onSaved: (data: ModelSettings) => void;
  onReload: () => void;
}) {
  const [model, setModel] = useState(current.model ?? "");
  const [protocol, setProtocol] = useState(current.protocol ?? "responses");
  const [enabled, setEnabled] = useState(current.enabled ?? false);
  const [temperature, setTemperature] = useState(current.send_temperature ?? true);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save(clear: boolean) {
    setBusy(true);
    setError("");
    try {
      const result = await saveModelSettings({
        provider: "onelinkai",
        protocol,
        base_url: "https://api.onelinkai.cloud/v1",
        model,
        send_temperature: temperature,
        enabled: clear ? false : enabled,
        revision: current.revision,
        clear_api_key: clear,
        ...(!clear && apiKey ? { api_key: apiKey } : {}),
      });
      onSaved(result);
    } catch {
      // Do not render arbitrary exception text: remote errors could echo a credential.
      setError("保存失败，未确认变更。请重新加载后检查模型、权限与系统凭据库，再重试。");
    } finally {
      setApiKey("");
      setBusy(false);
    }
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    void save(false);
  }
  return (
    <form className="model-settings-form" onSubmit={submit}>
      <fieldset disabled={busy} className="model-settings-fields">
        <div className="model-settings-grid">
          <div className="model-settings-field">
            <label htmlFor="model-protocol">请求协议</label>
            <select
              id="model-protocol"
              value={protocol}
              onChange={(e) => setProtocol(e.target.value as "responses" | "chat_completions")}
            >
              <option value="responses">Responses</option>
              <option value="chat_completions">Chat Completions</option>
            </select>
          </div>
          <div className="model-settings-field">
            <label htmlFor="model-base">网关地址</label>
            <input id="model-base" readOnly value="https://api.onelinkai.cloud/v1" />
          </div>
          <div className="model-settings-field">
            <label htmlFor="model-id">模型 ID</label>
            <input
              id="model-id"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              required
              maxLength={200}
              placeholder="填写账户实际可用模型"
            />
          </div>
          <div className="model-settings-field">
            <label htmlFor="api-key">API Key</label>
            <input
              id="api-key"
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              maxLength={1000}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={current.has_api_key ? "已保存，留空则保留" : "输入 OneLinkAI API Key"}
            />
          </div>
        </div>
        <label>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />{" "}
          启用真实模型（停用后不调用模型，生成入口不可用）
        </label>
        <label>
          <input
            type="checkbox"
            checked={temperature}
            onChange={(e) => setTemperature(e.target.checked)}
          />{" "}
          Responses 请求携带 temperature
        </label>
        <div className="model-settings-actions">
          <Button variant="primary" type="submit">
            {busy ? "保存中…" : "保存配置"}
          </Button>
          <Button
            disabled={!current.has_api_key}
            onClick={() => {
              if (window.confirm("确认清除已保存 API Key 并停用真实模型？")) void save(true);
            }}
          >
            清除 API Key 并停用
          </Button>
          <Button variant="quiet" onClick={onReload}>
            重新加载配置
          </Button>
        </div>
      </fieldset>
      <p className="inline-note">
        {current.has_api_key ? "API Key：已存入本机系统凭据库" : "API Key：未在此配置中保存"} ·
        未在线验证。保存不产生模型调用。
      </p>
      {/*
        「这一页存了什么」与「接下来会按什么跑」是两件事，后者只有后端算得出来。
        原先是 `!configured` 时补一句"当前运行沿用环境变量"——那句话没说清沿用到的
        究竟是真实模型还是假模型，而这两者的后果完全不同。改为直接呈现运行解析结果。
      */}
      <p className="inline-note" role="status">
        {runtimeSummary(current.runtime)}
      </p>
      {error && (
        <p role="alert" className="inline-note">
          {error}
        </p>
      )}
    </form>
  );
}
